/* أدوات بناء تذكير تحصيل احترافي عبر WhatsApp.
   - تنسيق رقم الهاتف العراقي إلى الصيغة الدولية (+964).
   - بناء رسالة عربية رسمية مع كَشف جنس المُتلقّي من اسمه.
   - الزرّ في الواجهة يَبقى رابطاً مباشراً (ضغطة واحدة → واتساب).
   لا تتطلّب أي مفتاح API ولا تكلفة LLM. */
import { honorific } from "./arabicGender";

/* علامة LEXIQ النصيّة الموحَّدة. الهويّة البصرية تَنتقل عبر النصّ نفسه —
   تَجنّبنا أيّ URL في النصّ لأنّ WhatsApp يَجلب معاينة قد تُشير لنطاق
   ليس مملوكاً لنا فيُسبّب تَشويشاً للعميل. */
export const LEXIQ_BRAND = "⚖️ *LEXIQ*";
export const LEXIQ_BOLD = "*LEXIQ*";
export const LEXIQ_PLAIN = "LEXIQ";
export const LEXIQ_WORDMARK = LEXIQ_BRAND;
export const LEXIQ_TAGLINE = "ذكاءٌ قانوني بمنطق القاضي ودِقّة المحامي";
/* رابط التطبيق الفعلي للمشاركة في WhatsApp/social — يَجب أن يُشير إلى
   الواجهة الأماميّة (lexiq.vip) لا الـ backend. WhatsApp يَجلب og:image
   من هذا الـ URL تلقائياً ويَعرض أيقونة الميزان فوق الرسالة.
   نَستَخدم window.location.origin كَمَصدر أوّليّ (دائماً صَحيح أينما
   فُتح التطبيق)، ثُمّ نطاق الإنتاج كَـfallback آمن. */
export const LEXIQ_URL =
  (typeof window !== "undefined" && window.location && window.location.origin)
    ? window.location.origin
    : "https://lexiq.vip";

/* فاصل بصري لطيف يَفصل المتن عن التذييل في رسائل واتساب. */
const SEP = "━━━━━━━━━━━━━━";

/** تطبيع رقم الهاتف العراقي للاستخدام في رابط wa.me. يرجع رقماً بدون "+" أو null. */
export function toWhatsappPhone(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return null;
  // 00964XXXXXXXXXX → 964XXXXXXXXXX
  if (digits.startsWith("00964")) return digits.slice(2);
  // 964XXXXXXXXXX → كما هو
  if (digits.startsWith("964")) return digits;
  // 07XXXXXXXXX → 9647XXXXXXXXX
  if (digits.startsWith("0")) return `964${digits.slice(1)}`;
  // أي شيء آخر: نضيف مفتاح العراق افتراضاً
  return `964${digits}`;
}

/** بناء نصّ تذكير رسمي مهذّب لعميل عليه مستحقّات — مع توقيع المحامي + شعار LEXIQ.
    - يَكتشف جنس المُتلقّي من اسمه (ذكر/أنثى) ويَستخدم الصيغة المُناسبة.
    - الرابط في الذيل = بوّابة العميل الشخصيّة (إن وُجدت) لكي:
        أ) WhatsApp يَجلب أيقونة LEXIQ من og:image تلقائياً.
        ب) إن ضَغط العميل المعاينة، يَنتقل لِصفحته الخاصّة (مستحقّاته)،
           ليس للتطبيق العام. */
export function buildReminderMessage({ name, due, caseTitle, lawyerName, portalUrl }) {
  const amount = (Number(due) || 0).toLocaleString("ar-IQ");
  const c = honorific(name);
  const l = honorific(lawyerName);
  const previewUrl = portalUrl || LEXIQ_URL;
  const lines = [
    `*تذكير ودّي بالمستحقّات المالية*`,
    "",
    `${c.title} ${c.addressee} ${c.respect}،`,
    "",
    "تحيّة طيّبة وبعد،",
    caseTitle
      ? `بخصوص قضيتكم: «${caseTitle}»، نودّ تذكيركم بلطف بوجود مستحقّات أتعاب قانونية متبقّية بقيمة:`
      : "نودّ تذكيركم بلطف بوجود مستحقّات أتعاب قانونية متبقّية بقيمة:",
    `*${amount} دينار عراقي*`,
    "",
    portalUrl
      ? "نرجو التكرّم بترتيب التسديد في أقرب فرصة ممكنة.\nيمكنكم متابعة كامل التفاصيل ومستحقّاتكم عبر بوّابتكم الخاصّة أدناه:"
      : "نرجو التكرّم بترتيب التسديد في أقرب فرصة ممكنة.",
    "شاكرين لكم حسن تعاونكم.",
    "",
    lawyerName ? `مع خالص التقدير،\n*${l.title === "الأستاذة" ? "المحامية" : (l.title === "الأستاذ" ? "المحامي" : "المحامي/ة")}:* ${lawyerName}` : "مع خالص التقدير.",
    "",
    SEP,
    `${LEXIQ_BRAND} — ${LEXIQ_TAGLINE}`,
    previewUrl,
  ];
  return lines.join("\n");
}

/** إنشاء رابط wa.me كامل جاهز للفتح في نافذة جديدة. */
export function buildWhatsappLink({ phone, name, due, caseTitle, lawyerName, portalUrl }) {
  const p = toWhatsappPhone(phone);
  if (!p) return null;
  const text = encodeURIComponent(
    buildReminderMessage({ name, due, caseTitle, lawyerName, portalUrl }),
  );
  return `https://wa.me/${p}?text=${text}`;
}

/** بناء نصّ دعوة العميل لفتح بوّابته عبر WhatsApp — مع كَشف جنس المُتلقّي. */
export function buildPortalInviteMessage({ name, portalUrl, lawyerName }) {
  const c = honorific(name);
  const l = honorific(lawyerName);
  const lawyerLabel = l.title === "الأستاذة" ? "المحامية" : (l.title === "الأستاذ" ? "المحامي" : "المحامي/ة");
  const lines = [
    `🔐 *بوّابتك الخاصّة على ${LEXIQ_PLAIN}*`,
    "",
    `${c.title} ${c.addressee} ${c.respect}،`,
    "",
    "تحيّة طيّبة،",
    `يمكنك الآن متابعة حالة قضيتك ومواعيدك ومستحقّاتك عبر بوّابتك الخاصّة:`,
    "",
    portalUrl,
    "",
    "⚠️ الرابط شخصي وآمن — يُرجى عدم مشاركته مع أي شخص.",
    "",
    lawyerName ? `مع التحيّة،\n*${lawyerLabel}:* ${lawyerName}` : null,
    "",
    SEP,
    `${LEXIQ_BRAND} — ${LEXIQ_TAGLINE}`,
  ].filter((x) => x !== null && x !== undefined);
  return lines.join("\n");
}

/** إنشاء رابط wa.me لدعوة العميل لفتح بوّابته. */
export function buildPortalInviteLink({ phone, name, portalUrl, lawyerName }) {
  const p = toWhatsappPhone(phone);
  if (!p || !portalUrl) return null;
  const text = encodeURIComponent(
    buildPortalInviteMessage({ name, portalUrl, lawyerName }),
  );
  return `https://wa.me/${p}?text=${text}`;
}

/* نُسخة "بدون رقم" — تفتح WhatsApp ويختار المستخدم جهة الاتصال يدوياً.
   مفيدة عندما لا يكون لدى المحامي رقم العميل محفوظاً في النظام. */
export function buildPortalInviteLinkGeneric({ name, portalUrl, lawyerName }) {
  if (!portalUrl) return null;
  const text = encodeURIComponent(
    buildPortalInviteMessage({ name, portalUrl, lawyerName }),
  );
  return `https://wa.me/?text=${text}`;
}

/* رسالة قصيرة بصيغة نصّيّة بسيطة بلا Markdown — مناسبة لِـ SMS وlistعن مَن
   ينسخها يدوياً إلى تطبيق آخر. */
export function buildPortalInvitePlain({ name, portalUrl, lawyerName }) {
  const c = honorific(name);
  const l = honorific(lawyerName);
  const lawyerLabel = l.title === "الأستاذة" ? "المحامية" : (l.title === "الأستاذ" ? "المحامي" : "المحامي/ة");
  const lines = [
    `${c.title} ${c.addressee} ${c.respect}،`,
    "بوّابتك الخاصّة على LEXIQ — متابعة قضيتك ومواعيدك ومستحقّاتك:",
    portalUrl,
    "الرابط شخصي وآمن — يُرجى عدم مشاركته.",
    lawyerName ? `${lawyerLabel}: ${lawyerName}` : null,
  ].filter((x) => x !== null && x !== undefined);
  return lines.join("\n");
}

/** SMS link (sms:) لدعوة العميل لفتح بوّابته. */
export function buildPortalInviteSmsLink({ phone, name, portalUrl, lawyerName }) {
  const p = toWhatsappPhone(phone);
  if (!p || !portalUrl) return null;
  const body = encodeURIComponent(
    buildPortalInvitePlain({ name, portalUrl, lawyerName }),
  );
  // RFC 5724: استخدام "+" قبل الرقم الدولي. iOS وAndroid يتعاملان مع `?body=`.
  return `sms:+${p}?body=${body}`;
}

/** mailto: لدعوة العميل لفتح بوّابته. */
export function buildPortalInviteMailLink({ email, name, portalUrl, lawyerName }) {
  if (!email || !portalUrl) return null;
  const subject = encodeURIComponent("بوّابتك الخاصّة على LEXIQ");
  const body = encodeURIComponent(
    buildPortalInvitePlain({ name, portalUrl, lawyerName }),
  );
  return `mailto:${email}?subject=${subject}&body=${body}`;
}

/** Telegram share URL لدعوة العميل لفتح بوّابته. */
export function buildPortalInviteTelegramLink({ name, portalUrl, lawyerName }) {
  if (!portalUrl) return null;
  const text = encodeURIComponent(
    buildPortalInvitePlain({ name, portalUrl, lawyerName }),
  );
  const url = encodeURIComponent(portalUrl);
  return `https://t.me/share/url?url=${url}&text=${text}`;
}
